import { useMemo, useState } from "react";

/**
 * Live size preview for label configurations.
 * Renders an SVG that scales the configured shape to fit inside a viewport,
 * maintaining the real-world aspect ratio. Shows dimension labels.
 *
 * Bleed & safe-zone overlay is togglable. Values follow print-industry
 * defaults: 0.125" bleed (outside die), 0.125" safe zone (inside die).
 */
const BLEED_IN = 0.125;
const SAFE_IN = 0.125;

export default function LiveSizePreview({ shape = "Rectangle", corners = "Rounded", heightIn, widthIn, color = "Full Color (CMYK) No White" }) {
  const h = Math.max(0.1, parseFloat(heightIn) || 2);
  const w = Math.max(0.1, parseFloat(widthIn) || 3);
  const [showBleed, setShowBleed] = useState(false);

  // Canvas dims (px). The actual label sits inside an inset.
  const CANVAS_W = 360;
  const CANVAS_H = 240;
  const INSET = 56;
  const innerW = CANVAS_W - INSET * 2;
  const innerH = CANVAS_H - INSET * 2;

  // Scale: fit real-world inches into innerW × innerH preserving aspect ratio.
  // When bleed is shown, factor its extra dims into the scale so nothing clips.
  const bleedFactor = showBleed ? BLEED_IN * 2 : 0;
  const scale = Math.min(innerW / (w + bleedFactor), innerH / (h + bleedFactor));
  const rectW = w * scale;
  const rectH = h * scale;
  const x = (CANVAS_W - rectW) / 2;
  const y = (CANVAS_H - rectH) / 2;
  const cx = CANVAS_W / 2;
  const cy = CANVAS_H / 2;
  const bleedPx = BLEED_IN * scale;
  const safePx = SAFE_IN * scale;

  // Determine fill / stroke based on color
  const palette = useMemo(() => {
    const c = (color || "").toLowerCase();
    if (c.includes("black & white")) return { fill: "#FFFFFF", stroke: "#0F172A", accent: "#0F172A" };
    if (c.includes("pantone")) return { fill: "#FEF3C7", stroke: "#333F48", accent: "#DD6B33" };
    if (c.includes("with white")) return { fill: "#FFFFFF", stroke: "#333F48", accent: "#333F48" };
    return { fill: "#FFFFFF", stroke: "#333F48", accent: "#333F48" }; // CMYK No White default
  }, [color]);

  const rxLabel = corners === "Square" ? 0 : Math.min(rectW, rectH) * 0.12;
  const shapeKey = (shape || "Rectangle").toLowerCase();

  // Helper that renders a shape at any offset/dims — reused for die, bleed, safe.
  const renderShape = (offX, offY, wpx, hpx, stroke, dash, fill = "none", key = "s") => {
    const strokeProps = { fill, stroke, strokeWidth: dash ? 1.4 : 2, strokeDasharray: dash || undefined };
    if (shapeKey === "circle") {
      const r = Math.min(wpx, hpx) / 2;
      return <circle key={key} cx={cx} cy={cy} r={r} {...strokeProps} />;
    }
    if (shapeKey === "oval") {
      return <ellipse key={key} cx={cx} cy={cy} rx={wpx / 2} ry={hpx / 2} {...strokeProps} />;
    }
    if (shapeKey === "square") {
      const side = Math.min(wpx, hpx);
      const sx = cx - side / 2;
      const sy = cy - side / 2;
      const sr = corners === "Square" ? 0 : side * 0.12;
      return <rect key={key} x={sx} y={sy} width={side} height={side} rx={sr} ry={sr} {...strokeProps} />;
    }
    // Rectangle
    const rr = corners === "Square" ? 0 : Math.min(wpx, hpx) * 0.12;
    return <rect key={key} x={offX} y={offY} width={wpx} height={hpx} rx={rr} ry={rr} {...strokeProps} />;
  };

  const dieShape = renderShape(x, y, rectW, rectH, palette.stroke, null, palette.fill, "die");
  const bleedShape = showBleed
    ? renderShape(x - bleedPx, y - bleedPx, rectW + bleedPx * 2, rectH + bleedPx * 2, "#DC2626", "4 3", "none", "bleed")
    : null;
  const safeShape = showBleed
    ? renderShape(x + safePx, y + safePx, rectW - safePx * 2, rectH - safePx * 2, "#0369A1", "3 3", "none", "safe")
    : null;

  // Dimension lines: width on top, height on left
  const widthY = y - 18;
  const heightX = x - 22;
  const tickH = 6;

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="live-size-preview">
      <div className="flex items-center justify-between mb-2 gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-slate-600">Live preview</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowBleed((v) => !v)}
            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border transition-colors ${
              showBleed
                ? "bg-kolt-slate-dark text-white border-kolt-slate-dark"
                : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"
            }`}
            aria-pressed={showBleed}
            data-testid="toggle-bleed-safe-btn"
          >
            {showBleed ? "Hide" : "Show"} Bleed / Safe
          </button>
          <div className="text-xs font-mono text-slate-500 hidden sm:block">
            {w}&quot; × {h}&quot;
          </div>
        </div>
      </div>
      <svg viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`} className="w-full h-auto" role="img" aria-label="label size preview">
        {/* Grid background */}
        <defs>
          <pattern id="grid" width="12" height="12" patternUnits="userSpaceOnUse">
            <path d="M 12 0 L 0 0 0 12" fill="none" stroke="#E2E8F0" strokeWidth="0.5" />
          </pattern>
          <pattern id="grid-major" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#CBD5E1" strokeWidth="1" />
          </pattern>
        </defs>
        <rect width={CANVAS_W} height={CANVAS_H} fill="url(#grid)" />
        <rect width={CANVAS_W} height={CANVAS_H} fill="url(#grid-major)" />

        {/* Bleed (outer, red dashed) — drawn under so die+safe overlay it cleanly */}
        {bleedShape}

        {/* Die (cut line) */}
        {dieShape}

        {/* Safe zone (inside, blue dashed) */}
        {safeShape}

        {/* Width dimension line (top) */}
        <g stroke="#94A3B8" strokeWidth="1">
          <line x1={x} y1={widthY} x2={x + rectW} y2={widthY} />
          <line x1={x} y1={widthY - tickH} x2={x} y2={widthY + tickH} />
          <line x1={x + rectW} y1={widthY - tickH} x2={x + rectW} y2={widthY + tickH} />
        </g>
        <text x={x + rectW / 2} y={widthY - 6} textAnchor="middle" className="font-mono" fontSize="11" fill="#475569" fontWeight="700">
          {w}&quot;
        </text>

        {/* Height dimension line (left) */}
        <g stroke="#94A3B8" strokeWidth="1">
          <line x1={heightX} y1={y} x2={heightX} y2={y + rectH} />
          <line x1={heightX - tickH} y1={y} x2={heightX + tickH} y2={y} />
          <line x1={heightX - tickH} y1={y + rectH} x2={heightX + tickH} y2={y + rectH} />
        </g>
        <text
          x={heightX - 8}
          y={cy}
          textAnchor="middle"
          className="font-mono"
          fontSize="11"
          fill="#475569"
          fontWeight="700"
          transform={`rotate(-90, ${heightX - 8}, ${cy})`}
        >
          {h}&quot;
        </text>

        {/* Center crosshair when shape is symmetrical */}
        {(shapeKey === "circle" || shapeKey === "oval") && (
          <g stroke={palette.accent} strokeWidth="0.5" strokeDasharray="3 2" opacity="0.6">
            <line x1={cx - 6} y1={cy} x2={cx + 6} y2={cy} />
            <line x1={cx} y1={cy - 6} x2={cx + 6} y2={cy} />
          </g>
        )}
      </svg>
      <div className="flex items-center justify-between mt-2">
        <div className="text-[10px] text-slate-500 font-mono">
          Area: {(w * h).toFixed(2)} sq in · Grid: 0.25&quot; approx
        </div>
        {showBleed && (
          <div className="flex items-center gap-3 text-[10px] font-mono text-slate-600" data-testid="bleed-safe-legend">
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-[2px] bg-[#DC2626]" style={{ borderTop: "2px dashed #DC2626", background: "transparent" }} />
              Bleed 0.125&quot;
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-[2px]" style={{ borderTop: "2px dashed #0369A1" }} />
              Safe 0.125&quot;
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
